// CardTableDisplay.parts.tsx
import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import { CardTableColumn } from './types';

// Composant Header partagé
interface CardTableHeaderProps {
  title: string;
  icon?: LucideIcon;
  iconColor?: string;
  titleAside?: ReactNode;
  headerClassName?: string;
  rightContent?: ReactNode;
  onClick?: () => void;
  clickable?: boolean;
}

export function CardTableHeader({
  title,
  icon,
  iconColor = 'text-primary',
  titleAside,
  headerClassName,
  rightContent,
  onClick,
  clickable = false
}: CardTableHeaderProps) {
  const Icon = icon;

  return (
    <div
      className={`flex items-center gap-3 px-6 py-4 bg-primary/5 border-b ${clickable ? 'cursor-pointer hover:bg-primary/10 transition-colors' : ''
        } ${headerClassName || ''}`}
      onClick={onClick}
    >
      {Icon && (
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10">
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      )}
      <h2 className="text-base font-semibold text-gray-800">
        {title}
      </h2>
      {titleAside && (
        <div className={rightContent ? 'mr-2' : 'ml-auto'}>
          {titleAside}
        </div>
      )}
      {rightContent && (
        <div className="ml-auto">
          {rightContent}
        </div>
      )}
    </div>
  );
}

// Composant Column Headers partagé
interface CardTableColumnHeadersProps<T> {
  columns: CardTableColumn<T>[];
  hasActions?: boolean;
}

export function CardTableColumnHeaders<T>({
  columns,
  hasActions
}: CardTableColumnHeadersProps<T>) {
  return (
    <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50/50 border-b">
      {columns.map((column) => (
        <div
          key={column.key}
          className={`${column.width} text-xs font-medium text-gray-500 uppercase tracking-wide ${column.headerClassName || ''
            }`}
        >
          {column.label}
        </div>
      ))}
      {hasActions && <div className="w-12"></div>}
    </div>
  );
}

// Composant Empty State partagé
interface CardTableEmptyStateProps {
  message: string;
}

export function CardTableEmptyState({ message }: CardTableEmptyStateProps) {
  return (
    <div className="px-6 py-12 text-center">
      <div className="text-gray-500 text-sm">
        {message}
      </div>
    </div>
  );
}

// Composant Rows partagé
interface CardTableRowsProps<T> {
  data: T[];
  columns: CardTableColumn<T>[];
  actions?: (item: T) => ReactNode;
  onItemClick?: (item: T) => void;
  rowClassName?: string | ((item: T) => string);
}

export function CardTableRows<T>({
  data,
  columns,
  actions,
  onItemClick,
  rowClassName
}: CardTableRowsProps<T>) {
  return (
    <div className="divide-y">
      {data.map((item, index) => {
        const computedRowClassName = typeof rowClassName === 'function'
          ? rowClassName(item)
          : rowClassName || '';

        return (
          <div
            key={index}
            className={`grid grid-cols-12 gap-4 px-6 py-4 transition-colors duration-150 ${onItemClick ? 'hover:bg-gray-50/50 cursor-pointer' : 'hover:bg-gray-50/50'
              } ${computedRowClassName}`}
            onClick={() => onItemClick?.(item)}
          >
            {/* Colonnes */}
            {columns.map((column) => (
              <div
                key={column.key}
                className={`${column.width} flex items-center`}
              >
                {column.render(item)}
              </div>
            ))}
            {/* Actions */}
            {actions && (
              <div className="flex items-center justify-end">
                {actions(item)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Composant Content partagé (headers + rows ou empty)
interface CardTableContentProps<T> {
  columns: CardTableColumn<T>[];
  data: T[];
  actions?: (item: T) => ReactNode;
  onItemClick?: (item: T) => void;
  rowClassName?: string | ((item: T) => string);
  emptyStateMessage: string;
}

export function CardTableContent<T>({
  columns,
  data,
  actions,
  onItemClick,
  rowClassName,
  emptyStateMessage
}: CardTableContentProps<T>) {
  return (
    <>
      <CardTableColumnHeaders columns={columns} hasActions={!!actions} />
      {data.length === 0 ? (
        <CardTableEmptyState message={emptyStateMessage} />
      ) : (
        <CardTableRows
          data={data}
          columns={columns}
          actions={actions}
          onItemClick={onItemClick}
          rowClassName={rowClassName}
        />
      )}
    </>
  );
}
